/**
 * Thin wrapper around the Telegram WebApp SDK. Safe to call outside Telegram
 * (returns no-op fallbacks) so the app can be developed in a normal browser.
 */
export function getTelegram(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp ?? null;
}

export function initTelegram(): void {
  const tg = getTelegram();
  if (!tg) return;
  tg.ready();
  tg.expand();
  // Bot API 8.0+ fullscreen: removes Telegram's drag-handle header and the
  // app fills the entire screen. Wrapped in try-catch since some clients
  // throw if the user has gestured Telegram into a state that forbids it.
  try {
    tg.requestFullscreen?.();
  } catch {
    /* fullscreen denied — fall back to expand() */
  }
  // Stops Telegram from swallowing downward swipes as "minimise" — otherwise
  // every scroll-up gesture risks collapsing the app to half height.
  try {
    tg.disableVerticalSwipes?.();
  } catch {
    /* not supported on this client version */
  }
  tg.setHeaderColor('#0F0F0F');
  tg.setBackgroundColor('#0F0F0F');
  try {
    tg.setBottomBarColor?.('#0F0F0F');
  } catch {
    /* older client */
  }

  // ----- Safe-area sync -----
  // In fullscreen mode CSS env(safe-area-inset-*) is unreliable across
  // Telegram clients — they don't propagate the WebView's iOS/Android viewport
  // insets correctly. Mirror Telegram's own insets into our CSS custom props
  // so notches and home-indicators never overlap content.
  syncSafeArea();
  try {
    tg.onEvent('safeAreaChanged', syncSafeArea);
    tg.onEvent('contentSafeAreaChanged', syncSafeArea);
    tg.onEvent('viewportChanged', syncSafeArea);
    tg.onEvent('fullscreenChanged', syncSafeArea);
  } catch {
    /* older client — no event subscriptions */
  }
}

function syncSafeArea(): void {
  const tg = getTelegram();
  if (!tg || typeof document === 'undefined') return;
  // Sum device + content insets — fullscreen we get the device notch from
  // safeAreaInset, while contentSafeAreaInset adds any in-app chrome.
  const top = (tg.safeAreaInset?.top ?? 0) + (tg.contentSafeAreaInset?.top ?? 0);
  const bottom =
    (tg.safeAreaInset?.bottom ?? 0) + (tg.contentSafeAreaInset?.bottom ?? 0);
  // Floor: even when Telegram reports 0 (Android phones without a software
  // home indicator) we want a meaningful gap so the nav and content don't
  // visually kiss the screen edge. 20 px ≈ standard iOS gesture margin.
  const safeTop = Math.max(top, 14);
  const safeBottom = Math.max(bottom, 20);
  const root = document.documentElement;
  root.style.setProperty('--safe-top', `${safeTop}px`);
  root.style.setProperty('--safe-bottom', `${safeBottom}px`);
}

export function getInitData(): string {
  return getTelegram()?.initData ?? '';
}

export function haptic(
  kind: 'success' | 'warning' | 'error' | 'selection' | 'light' | 'medium' | 'heavy' = 'light',
): void {
  // Respect the user's "Haptics" preference from Settings. The store lives in
  // localStorage under `premium-bot-ui` (zustand persist). We read it raw to
  // avoid creating a circular import (ui.store → telegram.ts is fine, but
  // telegram.ts is imported everywhere and we can't bring zustand here).
  if (!isHapticsEnabled()) return;
  const tg = getTelegram();
  if (!tg) return;
  try {
    if (kind === 'success' || kind === 'warning' || kind === 'error') {
      tg.HapticFeedback.notificationOccurred(kind);
    } else if (kind === 'selection') {
      tg.HapticFeedback.selectionChanged();
    } else {
      tg.HapticFeedback.impactOccurred(kind);
    }
  } catch {
    /* noop */
  }
}

function isHapticsEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem('premium-bot-ui');
    if (!raw) return true; // default on
    const parsed = JSON.parse(raw) as { state?: { haptics?: boolean } };
    return parsed.state?.haptics !== false;
  } catch {
    return true;
  }
}

export function showAlert(message: string): Promise<void> {
  return new Promise((resolve) => {
    const tg = getTelegram();
    if (tg) {
      tg.showAlert(message, () => resolve());
    } else {
      // eslint-disable-next-line no-alert
      window.alert(message);
      resolve();
    }
  });
}

export function showConfirm(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const tg = getTelegram();
    if (tg) {
      tg.showConfirm(message, (ok) => resolve(ok));
    } else {
      // eslint-disable-next-line no-alert
      resolve(window.confirm(message));
    }
  });
}
